import { useTranslation } from "react-i18next";

import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { z } from "zod";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { DEFAULT_C3_ASSET_CLASS_OPTIONS } from "./edit-form-conf";

const filterC3StaticDatumsSchema = z.object({
  name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  isin_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  code_contains: z.string().optional().nullable(),
  currency_contains: z.string().optional(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
});

export type FilterC3StaticDatumsSchema = z.infer<
  typeof filterC3StaticDatumsSchema
>;

const assetDepFields: readonly DepField<FilterC3StaticDatumsSchema>[] = [
  {
    name: "name_contains",
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  { name: "isin_contains", key: "isin", extract: "text", bucket: "equals" },
];

const TABLE_NAME = Tables.C3_STATIC_DATA;

export function FilterC3StaticData() {
  const { t, i18n } = useTranslation("tables");

  const fields: Field[] = [
    {
      name: "name_contains",
      label: t(`${TABLE_NAME}.searchbox.name_contains`, { defaultValue: "Name" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C3}/${Tables.C3_STATIC_DATA}`,
      column_name: "name",
      col_span: 2,
      filters: makeFiltersBuilder<FilterC3StaticDatumsSchema>(
        assetDepFields,
        "name_contains",
      ),
    },
    {
      name: "isin_contains",
      label: t(`${TABLE_NAME}.searchbox.isin_contains`, { defaultValue: "ISIN" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.C3}/${Tables.C3_STATIC_DATA}`,
      column_name: "isin",
      filters: makeFiltersBuilder<FilterC3StaticDatumsSchema>(
        assetDepFields,
        "isin_contains",
      ),
    },
    {
      name: "code_contains",
      label: t(`${TABLE_NAME}.searchbox.code_contains`, { defaultValue: "Code" }),
      type: "input",
    },
    {
      name: "currency_contains",
      label: t(`${TABLE_NAME}.searchbox.currency_contains`, { defaultValue: "Currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: "asset_class_1_equals",
      assetSubclassFieldName: "asset_class_2_equals",
      assetClassLabel: t(`${TABLE_NAME}.searchbox.asset_class_1_equals`, { defaultValue: "Asset class" }),
      assetSubclassLabel: t(`${TABLE_NAME}.searchbox.asset_class_2_equals`, { defaultValue: "Sub asset class" }),
      client: Clients.C3,
      staticDataTableName: Tables.C3_STATIC_DATA,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      assetSubclassOptions: DEFAULT_C3_ASSET_CLASS_OPTIONS,
    }),
  ];

  return (
    <FilterTable
      schema={filterC3StaticDatumsSchema}
      client={Clients.C3}
      tableName={Tables.C3_STATIC_DATA}
      fields={fields}
    />
  );
}
